/**
 * CardEditor — full editor for a single rate card: name/currency/description,
 * fallback rate/utilisation, roles table, and region/sector/size modifiers.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Plus, Save, Trash2, CheckCircle2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import InfoTooltip from "../InfoTooltip";
import NumInput from "./NumInput";
import ModifierGroup from "./ModifierGroup";

const TIERS = ["partner", "director", "manager", "senior_consultant", "consultant", "analyst", "specialist", "fallback"];

export default function CardEditor({ card, onSaved }) {
  const [draft, setDraft] = useState(card);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDraft(card); setDirty(false); }, [card]);

  const patch = (k, v) => { setDraft((d) => ({ ...d, [k]: v })); setDirty(true); };

  const updateRole = (i, p) => {
    const next = draft.roles.slice();
    next[i] = { ...next[i], ...p };
    patch("roles", next);
  };
  const addRole = () =>
    patch("roles", [...(draft.roles || []), { role: "New role", tier: "consultant", day_rate: 700, utilisation_pct: 80 }]);
  const removeRole = (i) => patch("roles", draft.roles.filter((_, idx) => idx !== i));

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/rate-cards/${card.id}`, {
        name: draft.name,
        description: draft.description,
        currency: draft.currency,
        roles: draft.roles,
        region_modifiers: draft.region_modifiers,
        sector_modifiers: draft.sector_modifiers,
        size_modifiers: draft.size_modifiers,
        fallback_day_rate: draft.fallback_day_rate,
        fallback_utilisation_pct: draft.fallback_utilisation_pct,
      });
      toast.success("Rate card saved");
      setDirty(false);
      onSaved?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }, [card.id, draft, onSaved]);

  return (
    <div className="space-y-4" data-testid="card-editor">
      <div className="bg-white border border-zinc-200 p-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] tracking-[0.18em] uppercase text-zinc-500 font-semibold">Name</label>
            <input
              value={draft.name || ""}
              onChange={(e) => patch("name", e.target.value)}
              className="w-full mt-1 border border-zinc-300 px-2 py-1.5 text-[13px] focus:outline-none focus:border-[#7737F6]"
              data-testid="card-name"
            />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.18em] uppercase text-zinc-500 font-semibold">Currency</label>
            <input
              value={draft.currency || "GBP"}
              onChange={(e) => patch("currency", e.target.value)}
              className="w-full mt-1 border border-zinc-300 px-2 py-1.5 text-[13px] focus:outline-none focus:border-[#7737F6]"
              data-testid="card-currency"
            />
          </div>
        </div>
        <div>
          <label className="text-[10px] tracking-[0.18em] uppercase text-zinc-500 font-semibold">Description</label>
          <textarea
            value={draft.description || ""}
            onChange={(e) => patch("description", e.target.value)}
            rows={2}
            className="w-full mt-1 border border-zinc-300 px-2 py-1.5 text-[12.5px] focus:outline-none focus:border-[#7737F6]"
            data-testid="card-description"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] tracking-[0.18em] uppercase text-zinc-500 font-semibold">
              Fallback day rate (for unknown roles)
              <InfoTooltip testid="rc-fallback-rate-info">Day-rate Claude Opus&rsquo;s unknown roles fall back to. Lower = stronger procurement defensibility. If any move hits this rate, the plan&rsquo;s cost-confidence drops one tier.</InfoTooltip>
            </label>
            <div className="mt-1">
              <NumInput value={draft.fallback_day_rate} onChange={(v) => patch("fallback_day_rate", v)} suffix="/d" min={100} max={5000} step={50} testid="card-fallback-rate"/>
            </div>
          </div>
          <div>
            <label className="text-[10px] tracking-[0.18em] uppercase text-zinc-500 font-semibold">
              Fallback utilisation
              <InfoTooltip testid="rc-fallback-util-info">Billable % assumed for fallback roles. Industry baseline 70&ndash;80%. Higher utilisation drops the effective day rate (more billable days per calendar week).</InfoTooltip>
            </label>
            <div className="mt-1">
              <NumInput value={draft.fallback_utilisation_pct} onChange={(v) => patch("fallback_utilisation_pct", v)} suffix="%" min={10} max={100} step={5} testid="card-fallback-util"/>
            </div>
          </div>
        </div>
      </div>

      {/* Roles table */}
      <div className="bg-white border border-zinc-200">
        <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-200 bg-zinc-50">
          <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-zinc-700">Roles & day-rates</div>
          <button
            type="button"
            onClick={addRole}
            className="text-[11px] text-[#7737F6] hover:text-[#5D28C7] inline-flex items-center gap-1"
            data-testid="role-add"
          >
            <Plus size={11}/> add role
          </button>
        </div>
        <table className="w-full text-[12.5px]">
          <thead className="text-[10px] uppercase tracking-[0.12em] text-zinc-500 bg-zinc-50">
            <tr>
              <th className="text-left px-3 py-1.5 font-semibold">Role</th>
              <th className="text-left px-3 py-1.5 font-semibold">Tier</th>
              <th className="text-right px-3 py-1.5 font-semibold">Day rate</th>
              <th className="text-right px-3 py-1.5 font-semibold">Utilisation</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {(draft.roles || []).map((r, i) => (
              <tr key={i} className="border-b border-zinc-100">
                <td className="px-3 py-1">
                  <input
                    value={r.role}
                    onChange={(e) => updateRole(i, { role: e.target.value })}
                    className="w-full border border-transparent hover:border-zinc-300 focus:border-[#7737F6] px-1 py-1 text-[12.5px] focus:outline-none"
                    data-testid={`role-name-${i}`}
                  />
                </td>
                <td className="px-3 py-1">
                  <select
                    value={r.tier || "consultant"}
                    onChange={(e) => updateRole(i, { tier: e.target.value })}
                    className="border border-zinc-300 px-1 py-1 text-[12px] focus:outline-none focus:border-[#7737F6]"
                    data-testid={`role-tier-${i}`}
                  >
                    {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </td>
                <td className="px-3 py-1 text-right">
                  <NumInput value={r.day_rate} onChange={(v) => updateRole(i, { day_rate: v })} suffix="/d" min={100} max={5000} step={50} testid={`role-rate-${i}`}/>
                </td>
                <td className="px-3 py-1 text-right">
                  <NumInput value={r.utilisation_pct} onChange={(v) => updateRole(i, { utilisation_pct: v })} suffix="%" min={10} max={100} step={5} testid={`role-util-${i}`}/>
                </td>
                <td className="px-3 py-1">
                  <button
                    type="button"
                    onClick={() => removeRole(i)}
                    className="text-zinc-400 hover:text-red-600 p-1"
                    aria-label="remove role"
                    data-testid={`role-remove-${i}`}
                  >
                    <Trash2 size={12}/>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ModifierGroup
        title="Region modifiers"
        items={draft.region_modifiers || []}
        onChange={(v) => patch("region_modifiers", v)}
        testidPrefix="region-mod"
        tooltip="Multiplier applied to every role&rsquo;s day rate based on the engagement&rsquo;s region (e.g. London 1.10, Regional UK 0.95). Match key matters: client.region is matched fuzzily against modifier.key."
      />
      <ModifierGroup
        title="Sector modifiers"
        items={draft.sector_modifiers || []}
        onChange={(v) => patch("sector_modifiers", v)}
        testidPrefix="sector-mod"
        tooltip="Multiplier reflecting sector-specific cost-to-serve (e.g. financial services 1.15 for regulatory scrutiny, public sector 0.90 for procurement caps)."
      />
      <ModifierGroup
        title="Size modifiers (engagement &amp; org size)"
        items={draft.size_modifiers || []}
        onChange={(v) => patch("size_modifiers", v)}
        testidPrefix="size-mod"
        tooltip="Multiplier for engagement / org complexity (e.g. global enterprise 1.10 for coordination overhead, mid-market 1.00 baseline)."
      />

      <div className="sticky bottom-0 bg-white border-t border-zinc-200 px-4 py-3 flex items-center justify-between">
        <div className="text-[11.5px] text-zinc-500">
          {dirty ? "Unsaved changes" : <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={12}/> Up to date</span>}
        </div>
        <button
          type="button"
          onClick={save}
          disabled={saving || !dirty}
          className="bg-[#7737F6] hover:bg-[#5D28C7] disabled:opacity-50 text-white text-[12.5px] font-medium px-4 py-1.5 inline-flex items-center gap-2"
          data-testid="card-save"
        >
          {saving ? <RefreshCw size={12} className="animate-spin"/> : <Save size={12}/>} Save changes
        </button>
      </div>
    </div>
  );
}
